import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus, Users, History, Bell } from "lucide-react";

const txColor = (p) => (p > 0 ? "text-mr-success" : "text-mr-accent");

export default function PatientProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [txs, setTxs] = useState([]);
  const [fams, setFams] = useState([]);
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [newFam, setNewFam] = useState({ name: "", relation: "Child", date_of_birth: "", phone: "" });

  const load = async () => {
    const [t, tx, f, n] = await Promise.all([
      api.get(`/tenants/by-slug/${slug}`),
      api.get("/patient/transactions"),
      api.get("/patient/family"),
      api.get("/patient/notifications"),
    ]);
    setTenant(t.data); setTxs(tx.data); setFams(f.data); setNotes(n.data);
  };
  useEffect(() => { load(); }, [slug]);

  const addFamily = async () => {
    try {
      await api.post("/patient/family", newFam);
      toast.success("Family member linked");
      setOpen(false);
      setNewFam({ name: "", relation: "Child", date_of_birth: "", phone: "" });
      await load();
    } catch { toast.error("Could not add family member"); }
  };

  const removeFamily = async (id) => {
    await api.delete(`/patient/family/${id}`);
    await load();
  };

  return (
    <div className="min-h-screen bg-mr-bg">
      <TopBar tenant={tenant} links={[
        { to: `/c/${slug}/dashboard`, label: "Dashboard" },
        { to: `/c/${slug}/rewards`, label: "Rewards" },
        { to: `/c/${slug}/family`, label: "Family" },
        { to: `/c/${slug}/refer`, label: "Refer" },
        { to: `/c/${slug}/profile`, label: "Profile" },
      ]} />

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <span className="label-eyebrow text-mr-accent">My Account</span>
          <h1 className="font-display text-4xl font-bold text-mr-primary mt-2">{user?.name}</h1>
          <p className="text-mr-secondary mt-1">{user?.email} · {user?.phone || "no phone"}</p>
        </div>

        <Tabs defaultValue="history" className="w-full">
          <TabsList className="bg-white border border-mr rounded-full p-1">
            <TabsTrigger data-testid="tab-history" value="history" className="rounded-full data-[state=active]:bg-mr-primary data-[state=active]:text-white"><History className="h-4 w-4 mr-2" /> History</TabsTrigger>
            <TabsTrigger data-testid="tab-family" value="family" className="rounded-full data-[state=active]:bg-mr-primary data-[state=active]:text-white"><Users className="h-4 w-4 mr-2" /> Family</TabsTrigger>
            <TabsTrigger data-testid="tab-notifications" value="notes" className="rounded-full data-[state=active]:bg-mr-primary data-[state=active]:text-white"><Bell className="h-4 w-4 mr-2" /> Notifications</TabsTrigger>
          </TabsList>

          <TabsContent value="history" className="mt-6">
            <div className="bg-white border border-mr rounded-2xl divide-y divide-mr-border">
              {txs.length === 0 && <p className="p-8 text-center text-mr-secondary text-sm">No transactions yet.</p>}
              {txs.map((tx) => (
                <div key={tx.id} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-mr-primary text-sm">{tx.reason}</p>
                    <p className="text-xs text-mr-secondary mt-0.5">{new Date(tx.created_at).toLocaleString()} · {tx.type}</p>
                  </div>
                  <span className={`font-display font-semibold ${txColor(tx.points)}`}>{tx.points > 0 ? "+" : ""}{tx.points}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="family" className="mt-6">
            <div className="flex justify-between mb-4">
              <p className="text-mr-secondary text-sm">Link family members so they can be associated with your account.</p>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-family-btn" className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]"><Plus className="h-4 w-4 mr-1" /> Add family member</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-display">Add family member</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Name</Label><Input data-testid="fam-name" value={newFam.name} onChange={(e) => setNewFam({ ...newFam, name: e.target.value })} /></div>
                    <div>
                      <Label>Relation</Label>
                      <Select value={newFam.relation} onValueChange={(v) => setNewFam({ ...newFam, relation: v })}>
                        <SelectTrigger data-testid="fam-relation"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Child", "Spouse", "Parent", "Sibling", "Other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Date of birth</Label><Input data-testid="fam-dob" type="date" value={newFam.date_of_birth} onChange={(e) => setNewFam({ ...newFam, date_of_birth: e.target.value })} /></div>
                    <div><Label>Phone</Label><Input data-testid="fam-phone" value={newFam.phone} onChange={(e) => setNewFam({ ...newFam, phone: e.target.value })} /></div>
                  </div>
                  <DialogFooter>
                    <Button data-testid="fam-save-btn" onClick={addFamily} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">Save</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {fams.length === 0 && <p className="text-mr-secondary col-span-full text-center py-8 text-sm">No family members linked yet.</p>}
              {fams.map((f) => (
                <div key={f.id} className="bg-white rounded-2xl border border-mr p-5 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-mr-primary">{f.name}</p>
                    <p className="text-xs text-mr-secondary">{f.relation}{f.date_of_birth ? ` · ${f.date_of_birth}` : ""}</p>
                  </div>
                  <Button data-testid={`fam-remove-${f.id}`} variant="ghost" size="icon" className="rounded-full" onClick={() => removeFamily(f.id)}><Trash2 className="h-4 w-4 text-mr-accent" /></Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="notes" className="mt-6">
            <div className="bg-white border border-mr rounded-2xl divide-y divide-mr-border">
              {notes.length === 0 && <p className="p-8 text-center text-mr-secondary text-sm">No notifications yet.</p>}
              {notes.map((n) => (
                <div key={n.id} className={`p-4 ${n.read ? "" : "bg-[#F8FAFC]"}`}>
                  <p className="font-medium text-mr-primary text-sm">{n.title}</p>
                  <p className="text-sm text-mr-secondary mt-0.5">{n.body}</p>
                  <p className="text-xs text-mr-secondary mt-1">{new Date(n.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
